import React, { useState, useEffect } from 'react';
import { Spin, message } from 'antd';
import api from '../services/api';

const GOLD = '#B8892A';
const INK = '#1A1410';
const INK3 = '#6B5840';
const INK4 = '#A89070';
const INK5 = '#D4C4A8';
const BG = '#FAF8F5';
const BG2 = '#F4F1EB';
const BD2 = '#EAE5DC';
const RED = '#9B2335';
const GREEN = '#2A6645';

function formatJPY(val) {
  if (val == null) return '--';
  return '\u00a5' + Number(val).toLocaleString('ja-JP');
}

export default function ValuationTool() {
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [enabled, setEnabled] = useState(null);
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    api.get('/lots/valuation-flags').then(r => {
      setEnabled(r.data?.valuation_enabled !== false);
    }).catch(() => setEnabled(true));
  }, []);

  if (enabled === false) {
    return (
      <div style={{ background: BG, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: INK3 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#128274;</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: INK, marginBottom: 8 }}>智能估价功能暂未开放</div>
          <div style={{ fontSize: 13 }}>该功能正在优化中，敬请期待</div>
        </div>
      </div>
    );
  }

  const doEstimate = async () => {
    if (!description && !imageUrl) {
      message.warning('请输入商品描述或图片链接');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post('/lots/ai-estimate', {
        description: description.trim(),
        image_url: imageUrl.trim() || undefined,
        category: category || undefined,
      });
      setResult(data);
    } catch (err) {
      message.error(err.response?.data?.error || '估价失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const confColor = { high: GREEN, medium: GOLD, low: INK4 };
  const confLabel = { high: '高', medium: '中', low: '低' };

  return (
    <div style={{ background: BG, minHeight: '100vh', padding: isMobile ? '16px' : '32px 24px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h1 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 700, color: INK, fontFamily: 'var(--serif)', margin: 0 }}>
            智能估价
          </h1>
          <p style={{ fontSize: 13, color: INK3, marginTop: 6 }}>
            输入商品描述或图片，AI 结合历史成交数据为您估算市场参考价
          </p>
        </div>

        {/* Input form */}
        <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${BD2}`, padding: isMobile ? 16 : 24, marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: INK3, display: 'block', marginBottom: 6 }}>
            商品描述 *
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="例：劳力士 Submariner 116610LN 黑盘 钢带 A品相 附全套&#10;或：爱马仕 Birkin 25 金棕色 Togo皮 金扣"
            style={{
              width: '100%', minHeight: 90, padding: 12, border: `1px solid ${BD2}`,
              borderRadius: 8, fontSize: 14, fontFamily: 'inherit', resize: 'vertical',
              outline: 'none', boxSizing: 'border-box',
            }}
          />

          <label style={{ fontSize: 12, fontWeight: 600, color: INK3, display: 'block', marginTop: 16, marginBottom: 6 }}>
            图片链接（可选，支持 AI 视觉识别）
          </label>
          <input
            value={imageUrl}
            onChange={e => setImageUrl(e.target.value)}
            placeholder="https://... 商品图片 URL"
            style={{
              width: '100%', padding: '10px 12px', border: `1px solid ${BD2}`,
              borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box',
            }}
          />

          <label style={{ fontSize: 12, fontWeight: 600, color: INK3, display: 'block', marginTop: 16, marginBottom: 6 }}>
            品类（可选）
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { key: '', label: '自动识别' },
              { key: 'watch', label: '手表' },
              { key: 'bag', label: '箱包' },
              { key: 'jewelry', label: '珠宝' },
              { key: 'art', label: '艺术品' },
            ].map(c => (
              <button key={c.key} onClick={() => setCategory(c.key)} style={{
                padding: '6px 16px', borderRadius: 16, fontSize: 12, fontWeight: 500,
                border: `1px solid ${category === c.key ? GOLD : BD2}`,
                background: category === c.key ? 'rgba(184,137,42,.1)' : '#fff',
                color: category === c.key ? GOLD : INK3,
                cursor: 'pointer',
              }}>
                {c.label}
              </button>
            ))}
          </div>

          <button
            onClick={doEstimate}
            disabled={loading}
            style={{
              width: '100%', marginTop: 20, padding: '12px 0',
              background: loading ? INK5 : GOLD, color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? 'AI 估价中...' : '开始估价'}
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, fontSize: 13, color: INK3 }}>AI 正在分析商品并查询市场数据...</div>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div style={{ background: '#fff', borderRadius: 12, border: `1.5px solid ${GOLD}`, overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', background: 'rgba(184,137,42,.06)', borderBottom: `1px solid ${BD2}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: INK }}>
                  估价结果
                </span>
                <span style={{ fontSize: 11, fontWeight: 600, color: confColor[result.confidence] || INK4 }}>
                  置信度: {confLabel[result.confidence] || '—'}
                </span>
              </div>
              {result.brand && (
                <div style={{ fontSize: 12, color: INK3, marginTop: 4 }}>
                  {result.brand} {result.model ? `/ ${result.model}` : ''} {result.estimated_grade ? `/ ${result.estimated_grade}` : ''}
                </div>
              )}
            </div>

            {/* Price */}
            <div style={{ padding: '20px' }}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: INK3, marginBottom: 4 }}>市场参考价范围</div>
                <div style={{ fontSize: isMobile ? 24 : 30, fontWeight: 700, color: GOLD, fontFamily: 'var(--serif)' }}>
                  {formatJPY(result.price_low_jpy)} ~ {formatJPY(result.price_high_jpy)}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: INK, marginTop: 4 }}>
                  参考均价: {formatJPY(result.price_avg_jpy)}
                </div>
                {result.cross_validated && (
                  <div style={{ fontSize: 11, color: GREEN, marginTop: 4 }}>
                    已与历史成交数据交叉验证
                  </div>
                )}
              </div>

              {/* Statistical reference */}
              {result.statistical_ref && (
                <div style={{ background: BG2, borderRadius: 8, padding: 12, marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: INK3, marginBottom: 6 }}>历史成交统计参考</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: INK }}>
                    <span>P25~P75: {formatJPY(result.statistical_ref.low)} ~ {formatJPY(result.statistical_ref.high)}</span>
                    <span>样本: {result.statistical_ref.samples}件</span>
                  </div>
                </div>
              )}

              {/* AI reasoning */}
              {result.reasoning_zh && (
                <div style={{ background: BG2, borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: INK3, marginBottom: 4 }}>AI 分析说明</div>
                  <div style={{ fontSize: 12, color: INK, lineHeight: 1.7 }}>{result.reasoning_zh}</div>
                </div>
              )}
            </div>

            <div style={{ padding: '8px 20px 14px', fontSize: 10, color: INK5 }}>
              * 估价基于 AI 分析 + 历史成交统计 + 外部市场数据，仅供参考，不构成出价建议
            </div>
          </div>
        )}

        {/* How it works */}
        <div style={{ marginTop: 24, background: '#fff', borderRadius: 12, border: `1px solid ${BD2}`, padding: isMobile ? 16 : 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 12 }}>估价原理</div>
          {[
            ['历史成交分析', '基于平台 2 万+ 历史成交数据，按品牌/型号/成色分层统计'],
            ['时间加权', '近期成交权重更高（90天半衰期），反映最新市场行情'],
            ['成色系数', '自动学习不同成色间的价格差，跨成色推算参考价'],
            ['AI 视觉识别', '通过商品图片识别品牌、型号、材质、成色'],
            ['外部数据', '融合 Yahoo拍卖、Mercari 等外部市场成交数据'],
            ['自动校准', '持续追踪预测准确度，自动修正系统性偏差'],
          ].map(([title, desc], i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
              <span style={{ width: 20, height: 20, borderRadius: '50%', background: BG2, textAlign: 'center', lineHeight: '20px', fontSize: 11, fontWeight: 600, color: GOLD, flexShrink: 0 }}>{i + 1}</span>
              <div>
                <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>{title}</span>
                <span style={{ fontSize: 11, color: INK4, marginLeft: 6 }}>{desc}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
